import cameraApiService from '../api/camera.api'
import cameraHealthApiService from '../api/camera-health.api'
import { CameraHealthStatus } from '../types/camera-health.types'
import { buildCameraHealthRows, CameraHealthRow } from '../utils/build-camera-health-rows'
import { Camera } from 'domain/camera'
import { useAuth } from 'layout/context/auth-context'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Tag } from 'primereact/tag'
import { useEffect, useMemo, useState } from 'react'
import './camera-health-view.scss'

const STATUS_SEVERITY: Record<
  CameraHealthRow['statusInfo']['status'],
  'success' | 'warning' | 'danger' | 'secondary' | 'info'
> = {
  healthy: 'success',
  silent: 'warning',
  'never-detected': 'danger',
  inactive: 'secondary',
  unknown: 'info',
}

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleString('pt-BR') : '—'

const CameraHealthView = () => {
  const { clientId } = useAuth()

  const [cameras, setCameras] = useState<Camera[]>([])
  const [health, setHealth] = useState<CameraHealthStatus[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) {
      return
    }

    setIsLoading(true)
    setError(null)

    Promise.all([
      cameraApiService.getCameras({ clientId }),
      cameraHealthApiService.getCameraHealth(clientId),
    ])
      .then(([cameraList, healthResponse]) => {
        setCameras(cameraList.responseObject ?? [])
        setHealth(healthResponse.responseObject?.cameras ?? [])
      })
      .catch(() => {
        setError(
          'Falha ao carregar a saúde das câmeras. Por favor, tente novamente.'
        )
      })
      .finally(() => setIsLoading(false))
  }, [clientId])

  const rows = useMemo(() => buildCameraHealthRows(cameras, health), [cameras, health])

  const healthyCount = rows.filter((r) => r.statusInfo.status === 'healthy').length
  const silentCount = rows.filter((r) => r.statusInfo.status === 'silent').length
  const neverDetectedCount = rows.filter((r) => r.statusInfo.status === 'never-detected').length

  return (
    <div className='camera-health-container'>
      <div className='camera-health-header'>
        <h2 className='camera-health-title'>
          <i className='pi pi-heart-fill' />
          Saúde das Câmeras
        </h2>
        <p className='camera-health-subtitle'>
          Última detecção de cada câmera — quando parou de enviar eventos, é
          sinal de que algo está errado antes que vire um alarme perdido.
        </p>
      </div>

      {!isLoading && !error && rows.length > 0 && (
        <div className='camera-health-summary'>
          <span className='camera-health-summary-chip camera-health-summary-chip--healthy'>
            <i className='pi pi-check-circle' />
            {healthyCount} normal{healthyCount === 1 ? '' : 'is'}
          </span>
          <span className='camera-health-summary-chip camera-health-summary-chip--silent'>
            <i className='pi pi-exclamation-triangle' />
            {silentCount} silenciosa{silentCount === 1 ? '' : 's'}
          </span>
          <span className='camera-health-summary-chip camera-health-summary-chip--never'>
            <i className='pi pi-times-circle' />
            {neverDetectedCount} sem detecção
          </span>
        </div>
      )}

      {isLoading ? (
        <div
          className='camera-health-state'
          data-testid='camera-health-loading'
        >
          <ProgressSpinner strokeWidth='4' />
        </div>
      ) : error ? (
        <div
          className='camera-health-state camera-health-state--error'
          data-testid='camera-health-error'
        >
          <i className='pi pi-exclamation-triangle' />
          <span>{error}</span>
        </div>
      ) : (
        <DataTable
          value={rows}
          dataKey='camera.id'
          className='camera-health-table'
          emptyMessage='Nenhuma câmera cadastrada.'
          sortField='statusInfo.status'
          sortOrder={-1}
          paginator={rows.length > 10}
          rows={10}
        >
          <Column
            field='camera.name'
            header='Câmera'
            sortable
            body={(row: CameraHealthRow) => (
              <div className='camera-health-name-cell'>
                <i className='pi pi-video' />
                {row.camera.name}
              </div>
            )}
          />
          <Column
            header='Status'
            sortable
            sortField='statusInfo.status'
            body={(row: CameraHealthRow) => (
              <Tag
                value={row.statusInfo.label}
                severity={STATUS_SEVERITY[row.statusInfo.status]}
              />
            )}
          />
          <Column
            field='camera.model.name'
            header='Modelo'
            body={(row: CameraHealthRow) => row.camera.model?.name ?? '—'}
          />
          <Column
            header='Última Detecção (Veículos)'
            body={(row: CameraHealthRow) => formatDate(row.health?.lastAnprEvent ?? null)}
          />
          <Column
            header='Última Detecção (Pessoas)'
            body={(row: CameraHealthRow) => formatDate(row.health?.lastAhcrEvent ?? null)}
          />
        </DataTable>
      )}
    </div>
  )
}

export default CameraHealthView
